import React, { useEffect, useState, useCallback, useRef } from "react";
import { Plus } from "lucide-react";
import { getDictByKind } from "../../../server/Category";
import { getSuppliers } from "../../../server/Supplier";
import { useNavigate, useLocation } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import ProductsFilters from "./ProductsFilters";
import ProductsTable from "./ProductsTable";
import {
  setFilters,
  clearFilters,
  setBaseProducts,
  reload,
} from "../../../server/ProductsFilters";
import { useProductsFilters } from "./UseProductsFilters";
import "./ProductsWithFilters.scss";
import {
  getProductsByStatus,
  getUncategorizedProducts,
} from "../../../server/Product";
import showAlert from "../../../server/ShowAlert.js";
import ListPagination from "../../containers/ListPagination";
import { useListPagination } from "../../../hooks/useListPagination";
import {
  resolveEditProductId,
  withResolvedEditProductId,
} from "../../../utils/productStatus";

const LIMIT = 4000;

const normalizeOrphanRow = (row) => {
  if (!row) return row;
  if (row.product) {
    return { ...row, product: withResolvedEditProductId(row.product) };
  }
  return withResolvedEditProductId(row);
};

const ProductsWithFilters = ({ openModal }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const stationId = useSelector((state) => state.user.stationId);
  const reloed = useSelector((state) => state.productFilters.reloed);
  const filters = useSelector((state) => state.productFilters.filters);
  const categoryChips = useSelector(
    (state) => state.productFilters.filters.categoryChips,
  );
  const [brand, setBrand] = useState([]);
  const [subBrand, setSubBrand] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [page, setPage] = useState(1);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [statusFilter, setStatusFilter] = useState([]);
  const [isOrphanView, setIsOrphanView] = useState(false);
  const [highlightProductId, setHighlightProductId] = useState(null);

  const browseSessionRef = useRef(0);
  const isServerSearchRef = useRef(false);
  const loadingMoreRef = useRef(false);
  const pendingScrollProductIdRef = useRef(
    location.state?.scrollToProductId || null,
  );

  const baseProducts = useProductsFilters(
    stationId,
    statusFilter,
    isOrphanView,
  );

  useEffect(() => {
    getSuppliers().then(setBrand).catch(console.error);
    getDictByKind("sub-brand").then(setSubBrand);
  }, []);

  useEffect(() => {
    if (!selectedProduct) return;

    const productId = resolveEditProductId(selectedProduct);
    if (!productId) {
      showAlert({
        msgType: "error",
        msgTitle: "לא ניתן לזהות את המוצר",
      });
      setSelectedProduct(null);
      return;
    }

    navigate(`/edit-product/${productId}/information`, {
      state: {
        from: "/admin/products",
        productId,
      },
    });
  }, [selectedProduct, navigate]);

  const isBrowseResultStale = useCallback((session) => {
    return session !== browseSessionRef.current || isServerSearchRef.current;
  }, []);

  const loadProducts = useCallback(
    async (pageToLoad, isFirstLoad = false) => {
      const session = browseSessionRef.current;

      try {
        if (isFirstLoad) {
          dispatch(reload());
        } else {
          setLoadingMore(true);
        }
        const res = await getProductsByStatus(stationId, {}, pageToLoad, LIMIT);
        if (isBrowseResultStale(session)) {
          return;
        }

        const items = Array.isArray(res) ? res : [];
        if (isFirstLoad) {
          dispatch(setBaseProducts(items));
        } else if (items.length) {
          dispatch(setBaseProducts([...(baseProducts || []), ...items]));
        }
        if (!items.length || items.length < LIMIT) {
          setHasMore(false);
        }
      } catch (err) {
        if (isBrowseResultStale(session)) {
          return;
        }
        setHasMore(false);
        if (isFirstLoad) {
          dispatch(setBaseProducts([]));
        }
        console.error("Error loading products:", err);
      } finally {
        if (!isBrowseResultStale(session)) {
          setLoadingMore(false);
        }
      }
    },
    [stationId, dispatch, baseProducts, isBrowseResultStale],
  );

  const searchTerm = filters.search?.trim() ?? "";
  const hasOtherFilters =
    filters.brandIds.length > 0 ||
    filters.subBrandIds.length > 0 ||
    !!filters.gtin ||
    categoryChips.length > 0 ||
    statusFilter.length > 0;
  const isServerSearch = searchTerm.length > 1 && !hasOtherFilters;

  isServerSearchRef.current = isServerSearch;

  useEffect(() => {
    if (isServerSearch || hasOtherFilters) {
      browseSessionRef.current += 1;
      setLoadingMore(false);
      loadingMoreRef.current = false;
    }
  }, [isServerSearch, hasOtherFilters]);

  useEffect(() => {
    if (isOrphanView && (hasOtherFilters || isServerSearch)) {
      setIsOrphanView(false);
    }
  }, [isOrphanView, hasOtherFilters, isServerSearch]);

  useEffect(() => {
    if (isOrphanView || hasOtherFilters || isServerSearch) {
      return;
    }

    browseSessionRef.current += 1;
    dispatch(reload());
    setPage(1);
    setHasMore(true);
    loadProducts(1, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    stationId,
    statusFilter,
    isServerSearch,
    isOrphanView,
    filters.brandIds,
    filters.subBrandIds,
    filters.gtin,
    categoryChips,
  ]);

  const filteredProducts = isServerSearch
    ? baseProducts
    : baseProducts?.filter((item) => {
        if (!searchTerm) return true;
        const p = item.product || item;
        return p.name?.toLowerCase().includes(searchTerm.toLowerCase());
      });

  const setProductsFromFilters = useCallback(
    (prods) => dispatch(setBaseProducts(prods)),
    [dispatch],
  );

  const handleClearFilters = useCallback(() => {
    setIsOrphanView(false);
    dispatch(clearFilters());
    setStatusFilter([]);
  }, [dispatch]);

  const showOrphans = useCallback(() => {
    browseSessionRef.current += 1;
    setIsOrphanView(true);
    dispatch(clearFilters());
    setStatusFilter([]);
    dispatch(reload());
    getUncategorizedProducts(stationId)
      .then((res) => {
        const products = Array.isArray(res?.products) ? res.products : [];
        dispatch(setBaseProducts(products.map(normalizeOrphanRow)));
      })
      .catch((err) => {
        console.error("Error fetching uncategorized products:", err);
        showAlert({
          msgType: "error",
          msgTitle: "אירעה שגיאה בטעינת המוצרים היתומים",
        });
      });
  }, [stationId, dispatch]);

  const {
    toShow,
    pCounter,
    cFrom,
    cTo,
    pTo,
    isRowVisible,
    showPagination,
    showPaggination,
    UpPaggination,
    downPaggination,
    toShowFun,
  } = useListPagination(filteredProducts?.length ?? 0);

  useEffect(() => {
    const scrollToProductId = pendingScrollProductIdRef.current;
    if (!scrollToProductId || reloed || !filteredProducts?.length) return;

    const index = filteredProducts.findIndex((row) => {
      const p = row.product || row;
      return String(resolveEditProductId(p)) === String(scrollToProductId);
    });
    if (index < 0) return;

    pendingScrollProductIdRef.current = null;
    const pageIndex = Math.floor(index / toShow);
    showPaggination(pageIndex);
    setHighlightProductId(String(scrollToProductId));

    if (location.state?.scrollToProductId) {
      navigate(location.pathname, { replace: true, state: {} });
    }

    const timer = window.setTimeout(() => {
      const el = document.querySelector(
        `[data-product-id="${CSS.escape(String(scrollToProductId))}"]`,
      );
      el?.scrollIntoView({ block: "center", behavior: "smooth" });
    }, 50);

    const clearHighlight = window.setTimeout(() => {
      setHighlightProductId(null);
    }, 2500);

    return () => {
      window.clearTimeout(timer);
      window.clearTimeout(clearHighlight);
    };
  }, [
    filteredProducts,
    reloed,
    toShow,
    showPaggination,
    navigate,
    location.pathname,
    location.state?.scrollToProductId,
  ]);

  const filteredCount = filteredProducts?.length ?? 0;
  const canLoadMoreFromApi =
    !isOrphanView &&
    !searchTerm &&
    filters.brandIds.length === 0 &&
    filters.subBrandIds.length === 0 &&
    !filters.gtin &&
    categoryChips.length === 0 &&
    statusFilter.length === 0;

  useEffect(() => {
    if (
      !canLoadMoreFromApi ||
      loadingMore ||
      !hasMore ||
      !filteredCount ||
      pTo < filteredCount
    ) {
      return;
    }

    if (loadingMoreRef.current) return;
    loadingMoreRef.current = true;

    const nextPage = page + 1;
    setPage(nextPage);
    loadProducts(nextPage).finally(() => {
      loadingMoreRef.current = false;
    });
  }, [
    canLoadMoreFromApi,
    loadingMore,
    hasMore,
    filteredCount,
    pTo,
    page,
    loadProducts,
  ]);

  return (
    <div className="products-page admin-page admin-page--fill">
      <header className="admin-page__header admin-page__header--flat">
        <div className="admin-page__header-start">
          <h1 className="title-role">ניהול מוצרים</h1>
        </div>
        <div className="admin-page__header-end">
          <button
            type="button"
            className="products-action-btn products-action-btn--primary"
            onClick={() => openModal()}
            title="יצירת מוצר"
            aria-label="יצירת מוצר חדש"
          >
            <Plus aria-hidden size={18} strokeWidth={2} />
            <span>יצירת מוצר</span>
          </button>
        </div>
      </header>
      <ProductsFilters
        brand={brand}
        subBrand={subBrand}
        filters={filters}
        setFilters={(payload) => dispatch(setFilters(payload))}
        setProducts={setProductsFromFilters}
        clearFilters={handleClearFilters}
        statusFilter={statusFilter}
        setStatusFilter={setStatusFilter}
        filteredProducts={filteredProducts}
        openModal={openModal}
        onShowOrphans={showOrphans}
      />
      <div className="admin-page__body products-body">
        {reloed ? (
          <p>טוען...</p>
        ) : filteredProducts?.length ? (
          <>
            <ListPagination
              className="products-pagination"
              showPagination={showPagination}
              toShow={toShow}
              toShowFun={toShowFun}
              cFrom={cFrom}
              cTo={cTo}
              pCounter={pCounter}
              showPaggination={showPaggination}
              downPaggination={downPaggination}
              UpPaggination={UpPaggination}
            />
            <ProductsTable
              products={filteredProducts}
              onSelect={setSelectedProduct}
              isRowVisible={isRowVisible}
              highlightProductId={highlightProductId}
            />
            {loadingMore && (
              <div style={{ textAlign: "center", padding: "20px" }}>
                טוען עוד...
              </div>
            )}
          </>
        ) : (
          <p>לא נמצאו מוצרים עם הקריטריונים שנבחרו</p>
        )}
      </div>
    </div>
  );
};

export default ProductsWithFilters;
